import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Drawer } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBookOpen, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import { FunctionalRootState } from '@share/utils';

import type { IAccountGuide } from '../../guides';
import type { IAccountSection } from '../../types';
import { getAccountSectionPath, getHubThemeStyle } from '../../utils';

import styles from './styles.module.scss';

// Below this width the guide drawer takes the full screen instead of a fixed 480px panel.
const PHONE_QUERY = '(max-width: 575px)';
const DRAWER_WIDTH = 480;

const useIsPhone = (): boolean => {
  const [isPhone, setIsPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches);

  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsPhone(event.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return isPhone;
};

interface IProps {
  guides: IAccountGuide[];
  sections: IAccountSection[];
  accountUsername: string;
}

/**
 * "Help and guides": one row of cards across the full width, scrolled sideways (snap, arrows) instead
 * of stacking; each opens a short step-by-step guide in a side drawer.
 */
export const AccountGuides = ({ guides, sections, accountUsername }: IProps): React.ReactElement | null => {
  const intl = useIntl();
  const [openId, setOpenId] = useState<IAccountGuide['id'] | null>(null);

  // Carousel: the arrows show only when there is more to scroll that way.
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    setCanPrev(track.scrollLeft > 4);
    setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
  }, []);
  // The row's width settles while the page loads, and scroll snapping can then land on a later card.
  // Until the member scrolls it themselves, keep it on the first guide.
  const touched = useRef(false);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      if (!touched.current) {
        track.scrollLeft = 0;
      }
      updateArrows();
    });
    observer.observe(track);
    return () => observer.disconnect();
  }, [updateArrows, guides.length]);
  const scrollByCard = (direction: 1 | -1) => {
    touched.current = true;
    const track = trackRef.current;
    const card = track?.querySelector('li');
    if (track && card) {
      track.scrollBy({ left: direction * (card.getBoundingClientRect().width + 12), behavior: 'smooth' });
    }
  };

  if (!guides.length) {
    return null;
  }

  const text = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const open = guides.find(guide => guide.id === openId) || null;

  return (
    <section aria-labelledby="account-hub-guides-title">
      <div className={styles.guidesHeader}>
        <h2 id="account-hub-guides-title" className={styles.label}>{text('my.account.hub.guides.title')}</h2>
        {canPrev || canNext ? (
          <div className={styles.guidesNav}>
            <button
              type="button"
              className={styles.guidesArrow}
              disabled={!canPrev}
              onClick={() => scrollByCard(-1)}
              aria-label={text('my.account.hub.guides.prev')}
            >
              <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={styles.guidesArrow}
              disabled={!canNext}
              onClick={() => scrollByCard(1)}
              aria-label={text('my.account.hub.guides.next')}
            >
              <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
      <ul
        ref={trackRef}
        className={styles.guides}
        onScroll={updateArrows}
        onPointerDown={() => {
          touched.current = true;
        }}
        onWheel={() => {
          touched.current = true;
        }}
      >
        {guides.map(guide => (
          <li key={guide.id}>
            <button type="button" className={styles.guide} onClick={() => setOpenId(guide.id)}>
              <span className={styles.guideIcon} aria-hidden="true"><FontAwesomeIcon icon={faBookOpen} /></span>
              <span className={styles.guideText}>
                <span className={styles.guideTitle}>{text(`my.account.hub.guide.${guide.id}.title`)}</span>
                <span className={styles.guideSummary}>{text(`my.account.hub.guide.${guide.id}.summary`)}</span>
              </span>
              <span className={styles.guideMeta}>{text('my.account.hub.guides.read_time', { minutes: guide.readMinutes })}</span>
            </button>
          </li>
        ))}
      </ul>

      <AccountGuideDrawer
        guide={open}
        sections={sections}
        accountUsername={accountUsername}
        onClose={() => setOpenId(null)}
      />
    </section>
  );
};

interface IDrawerProps {
  guide: IAccountGuide | null;
  sections: IAccountSection[];
  accountUsername: string;
  onClose: () => void;
  /** Off when the drawer is opened from the page its action would open. */
  showAction?: boolean;
}

/** One guide, step by step, in a side drawer. Also opened from a page's intro (e.g. Cash out). */
export const AccountGuideDrawer = ({ guide, sections, accountUsername, onClose, showAction = true }: IDrawerProps): React.ReactElement => {
  const intl = useIntl();
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);
  const isPhone = useIsPhone();
  const text = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const open = guide;
  const actionSection = showAction && open?.actionSection ? sections.find(section => section.id === open.actionSection) : undefined;

  return (
    <Drawer
      open={!!open}
      onClose={onClose}
      placement="right"
      width={isPhone ? '100%' : DRAWER_WIDTH}
      zIndex={4100}
      title={open ? text(`my.account.hub.guide.${open.id}.title`) : ''}
      className={styles.guideDrawer}
      style={getHubThemeStyle(account)}
      destroyOnClose
    >
      {open ? (
        <article className={styles.guideArticle}>
          <p className={styles.guideLead}>{text(`my.account.hub.guide.${open.id}.summary`)}</p>
          <ol className={styles.guideSteps}>
            {Array.from({ length: open.steps }, (_, index) => (
              <li key={index}>{text(`my.account.hub.guide.${open.id}.step${index + 1}`)}</li>
            ))}
          </ol>
          {actionSection ? (
            <Link
              to={getAccountSectionPath(accountUsername, actionSection)}
              className={styles.guideAction}
              onClick={onClose}
            >
              {text(`my.account.hub.guide.${open.id}.action`)}
              <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </Link>
          ) : null}
        </article>
      ) : null}
    </Drawer>
  );
};
